// FO-003 (batch-12) — Interne start-workflow nieuwe medewerker.
//
// Types voor (a) de configureerbare standaard verplichte-certificatenlijst en
// (b) de cockpit die de live status uit alle bestaande modules samenvat. De
// cockpit is een afgeleide VIEW (geen eigen persistente stap-records): de status
// komt uit `bepaalTypeStatus` (certificaten) + de materieel-/uitrusting-/voorraad-
// modules; de actie-stappen leven als OperationeleTaak.

import { z } from 'zod'
import { idParam } from './id-validators'

// ─── Relatieve deadline ─────────────────────────────────────────────────────

export const ONBOARDING_CERTIFICAAT_DEADLINE = [
  'WEEK_1',
  'DRIE_MAANDEN',
  'TWAALF_MAANDEN',
  'GEEN',
] as const
export type OnboardingCertificaatDeadline =
  (typeof ONBOARDING_CERTIFICAAT_DEADLINE)[number]

export const ONBOARDING_CERTIFICAAT_DEADLINE_LABELS: Record<
  OnboardingCertificaatDeadline,
  string
> = {
  WEEK_1: '1e week',
  DRIE_MAANDEN: 'Binnen 3 maanden',
  TWAALF_MAANDEN: 'Binnen 12 maanden',
  GEEN: 'Geen deadline',
}

/**
 * Aantal kalenderdagen na de startdatum per deadline-type (FO-003 §11-keuze:
 * week 1 = 7, 3 maanden = 90, 12 maanden = 365). `GEEN` → null (geen deadline).
 * De berekening zelf (Europe/Amsterdam, dag-precisie) staat in @streep/utils.
 */
export const ONBOARDING_CERTIFICAAT_DEADLINE_DAGEN: Record<
  OnboardingCertificaatDeadline,
  number | null
> = {
  WEEK_1: 7,
  DRIE_MAANDEN: 90,
  TWAALF_MAANDEN: 365,
  GEEN: null,
}

// ─── Config-DTO + Zod (verplichte-certificatenlijst) ────────────────────────

export interface OnboardingVerplichtCertificaatDTO {
  id: string
  certificateTypeId: string
  certificateTypeNaam: string
  certificateTypeCode: string
  deadlineType: OnboardingCertificaatDeadline
  verplicht: boolean
  volgorde: number
  actief: boolean
  isVoorbeeld: boolean
}

export const createOnboardingVerplichtCertificaatSchema = z.object({
  certificateTypeId: idParam('certificateTypeId'),
  deadlineType: z.enum(ONBOARDING_CERTIFICAAT_DEADLINE).default('GEEN'),
  verplicht: z.boolean().default(true),
  volgorde: z.number().int().min(0).default(0),
})
export type CreateOnboardingVerplichtCertificaatInput = z.infer<
  typeof createOnboardingVerplichtCertificaatSchema
>

export const updateOnboardingVerplichtCertificaatSchema = z.object({
  deadlineType: z.enum(ONBOARDING_CERTIFICAAT_DEADLINE).optional(),
  verplicht: z.boolean().optional(),
  volgorde: z.number().int().min(0).optional(),
  actief: z.boolean().optional(),
})
export type UpdateOnboardingVerplichtCertificaatInput = z.infer<
  typeof updateOnboardingVerplichtCertificaatSchema
>

// ─── Cockpit-DTO (live status uit alle modules) ─────────────────────────────

/** Afgeleide certificaat-status in de cockpit (samenvatting van bepaalTypeStatus). */
export type StartWorkflowCertStatus =
  | 'HEEFT' // ACTIEF / VERLOOPT_BINNENKORT
  | 'INGEPLAND' // GEPLAND (cursus staat gepland)
  | 'IN_OPLEIDING' // sessie loopt
  | 'WACHT' // WACHT_OP_CERTIFICAAT (sessie afgerond, cert nog niet aangemaakt)
  | 'ONTBREEKT' // ONTBREEKT / VERLOPEN
  | 'NVT' // NIET_VAN_TOEPASSING

export interface StartWorkflowCertItemDTO {
  certificateTypeId: string
  naam: string
  code: string
  deadlineType: OnboardingCertificaatDeadline
  verplicht: boolean
  status: StartWorkflowCertStatus
  /** Of het certificaat als "klaar" telt (HEEFT of NVT). */
  klaar: boolean
  /** ISO-datum (yyyy-mm-dd) van de relatieve deadline, of null (deadlineType GEEN of geen startdatum). */
  deadline: string | null
  /** Kalenderdagen tot de deadline (negatief = verstreken); null als er geen deadline is. */
  dagenTotDeadline: number | null
  /** true als de deadline verstreken is én het certificaat nog niet behaald. */
  overschreden: boolean
  /** Titel van de geplande/lopende cursus (als INGEPLAND/IN_OPLEIDING), anders null. */
  geplandeCursus: string | null
  /** ISO-datum van de geplande sessie, anders null. */
  geplandeDatum: string | null
}

export type StartWorkflowResourceKey =
  | 'VOERTUIG'
  | 'KLEDING'
  | 'TELEFOON_SIM'
  | 'GEREEDSCHAP'

export const START_WORKFLOW_RESOURCE_LABELS: Record<
  StartWorkflowResourceKey,
  string
> = {
  VOERTUIG: 'Voertuig + sleutel',
  KLEDING: 'Werkkleding (Van Ysselt)',
  TELEFOON_SIM: 'Telefoon + SIM',
  GEREEDSCHAP: 'Gereedschap',
}

export type StartWorkflowResourceStatus =
  | 'TOEGEWEZEN' // definitief toegewezen/uitgereikt
  | 'BESTELD' // besteld/gereserveerd en onderweg
  | 'GERESERVEERD' // gereserveerd tijdens onboarding
  | 'ONTBREEKT' // nog niets

export interface StartWorkflowResourceItemDTO {
  key: StartWorkflowResourceKey
  label: string
  status: StartWorkflowResourceStatus
  klaar: boolean
  /** Mensvriendelijke detailregel (kenteken, telefoonnummer, bestelstatus, ...). */
  detail: string | null
}

export interface StartWorkflowTaakDTO {
  id: string
  titel: string
  status: string
  /** bronSubKey: welke stap deze taak vertegenwoordigt (CERTIFICATEN/VOERTUIG/...). */
  stap: string | null
  deadline: string | null
  uitvoerderNaam: string | null
}

export interface StartWorkflowCockpitDTO {
  onboardingId: string
  onboardingStatus: string
  employeeId: string
  medewerkerNaam: string
  functie: string | null
  startDatum: string | null
  /** true zodra de start-workflow gestart is (Onboarding.startWorkflowGestartOp gezet). */
  gestart: boolean
  gestartOp: string | null
  /** Voortgang: aantal afgeronde stappen / totaal (verplichte certs + middelen). */
  stappenKlaar: number
  stappenTotaal: number
  certificaten: StartWorkflowCertItemDTO[]
  resources: StartWorkflowResourceItemDTO[]
  taken: StartWorkflowTaakDTO[]
}
